import { useForm } from '@inertiajs/react'
import AppLayout from '@/components/layouts/AppLayout'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import type { DeploymentTypeOption } from '@/types'

interface Props {
  vendor: { id: number; name: string; slug: string }
  app_entry: { id: number; name: string; slug: string }
  release: { id: number; version: string; status: string; editable: boolean }
  deployment_types: DeploymentTypeOption[]
}

export default function DeploymentPackagesNew({ vendor, app_entry, release, deployment_types }: Props) {
  const basePath = `/vendors/${vendor.slug}/app_entries/${app_entry.slug}/releases/${release.id}`
  const { data, setData, post, processing, errors } = useForm({
    deployment_package: {
      name: '',
      description: '',
      deployment_type: 'docker_compose',
      variant_label: '',
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`${basePath}/deployment_packages`)
  }

  return (
    <AppLayout>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          onClick={() => window.history.back()}
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5">New Deployment Package</Typography>
          <Typography variant="body2" color="text.secondary">
            {vendor.name} / {app_entry.name} / v{release.version}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Name"
            fullWidth
            required
            margin="normal"
            value={data.deployment_package.name}
            onChange={(e) =>
              setData('deployment_package', { ...data.deployment_package, name: e.target.value })
            }
            error={!!errors['deployment_package.name']}
            helperText={errors['deployment_package.name'] || 'A descriptive name for this deployment package'}
          />

          <TextField
            label="Deployment Type"
            fullWidth
            required
            select
            margin="normal"
            value={data.deployment_package.deployment_type}
            onChange={(e) =>
              setData('deployment_package', { ...data.deployment_package, deployment_type: e.target.value })
            }
            error={!!errors['deployment_package.deployment_type']}
            helperText={errors['deployment_package.deployment_type']}
          >
            {deployment_types.map((dt) => (
              <MenuItem key={dt.value} value={dt.value}>
                {dt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Variant Label"
            fullWidth
            margin="normal"
            value={data.deployment_package.variant_label}
            onChange={(e) =>
              setData('deployment_package', { ...data.deployment_package, variant_label: e.target.value })
            }
            error={!!errors['deployment_package.variant_label']}
            helperText={errors['deployment_package.variant_label'] || 'Optional label to distinguish variants of the same type'}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            margin="normal"
            value={data.deployment_package.description}
            onChange={(e) =>
              setData('deployment_package', { ...data.deployment_package, description: e.target.value })
            }
            error={!!errors['deployment_package.description']}
            helperText={errors['deployment_package.description']}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={processing}
            >
              Create Package
            </Button>
          </Box>
        </form>
      </Paper>
    </AppLayout>
  )
}
